import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import api from '../services/api';
import {
  ArrowLeftIcon,
  PrinterIcon,
  ShareIcon,
  LockClosedIcon,
  LockOpenIcon,
  XCircleIcon,
  QrCodeIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

const fetchBill = (id) => api.get(`/bills/${id}`).then(res => res.data.data.bill);

export default function BillDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: bill, isLoading } = useQuery(['bill', id], () => fetchBill(id));

  const issueBill = useMutation(
    () => api.post(`/bills/${id}/issue`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['bill', id]);
        toast.success('Bill issued successfully');
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Failed to issue bill')
    }
  );

  const toggleBlock = useMutation(
    (action) => api.post(`/bills/${id}/block`, { action, reason: 'Administrative action' }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['bill', id]);
        toast.success(`Bill ${bill.status === 'blocked' ? 'unblocked' : 'blocked'} successfully`);
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Failed to update bill')
    }
  );

  const cancelBill = useMutation(
    () => api.post(`/bills/${id}/cancel`, { reason: 'Administrative cancellation' }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['bill', id]);
        toast.success('Bill cancelled successfully');
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Failed to cancel bill')
    }
  );

  const generateSecureLink = useMutation(
    () => api.post(`/bills/${id}/secure-link`, { type: 'bill_view', expiryHours: 24 }),
    {
      onSuccess: (res) => {
        toast.success('Secure link generated');
        navigator.clipboard.writeText(res.data.data.link);
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Failed to generate link')
    }
  );

  if (isLoading) return <div className="text-center py-8">Loading...</div>;
  if (!bill) return <div className="text-center py-8">Bill not found</div>;

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      issued: 'bg-blue-100 text-blue-800',
      unpaid: 'bg-yellow-100 text-yellow-800',
      paid: 'bg-green-100 text-green-800',
      blocked: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800',
      expired: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <>
      {/* On-screen view - hidden during print */}
      <div className="space-y-6 print:hidden">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/bills')} className="p-2 hover:bg-slate-100 rounded-full">
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Bill Details</h1>
            <p className="text-sm text-slate-500">{bill.billId}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            <div className="card">
              <div className="card-header flex justify-between items-center">
                <h3 className="text-lg font-medium">Bill Information</h3>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(bill.status)}`}>
                  {bill.status.toUpperCase()}
                </span>
              </div>
              <div className="card-body">
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm font-medium text-slate-500">Challan Number</dt>
                    <dd className="mt-1 text-lg font-semibold">{bill.challanNo}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-slate-500">Consumer Name</dt>
                    <dd className="mt-1 text-lg font-semibold">{bill.consumerName}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-slate-500">Due Date</dt>
                    <dd className="mt-1">{new Date(bill.dueDate).toLocaleDateString()}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-slate-500">Billing Month</dt>
                    <dd className="mt-1">{bill.billingMonth}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-slate-500">Amount Before Due Date</dt>
                    <dd className="mt-1 text-lg font-semibold text-green-600">PKR {parseFloat(bill.feeBeforeDueDate).toLocaleString()}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-slate-500">Amount After Due Date</dt>
                    <dd className="mt-1 text-lg font-semibold text-red-600">PKR {parseFloat(bill.feeAfterDueDate).toLocaleString()}</dd>
                  </div>
                </dl>
              </div>
            </div>

            {/* Status History */}
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-medium">Status History</h3>
              </div>
              <div className="card-body">
                <div className="space-y-4">
                  {bill.statusHistory?.map((history, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="mt-1">
                        {history.newStatus === 'paid' ? (
                          <CheckCircleIcon className="h-5 w-5 text-green-500" />
                        ) : history.newStatus === 'blocked' ? (
                          <LockClosedIcon className="h-5 w-5 text-red-500" />
                        ) : (
                          <div className="h-5 w-5 rounded-full bg-slate-200" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium">
                          {history.previousStatus ? `${history.previousStatus} → ${history.newStatus}` : history.newStatus}
                        </p>
                        <p className="text-xs text-slate-500">{history.reason}</p>
                        <p className="text-xs text-slate-400">{new Date(history.createdAt).toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* QR Code */}
            {bill.qrCode && (
              <div className="card">
                <div className="card-header">
                  <h3 className="text-lg font-medium flex items-center gap-2">
                    <QrCodeIcon className="h-5 w-5" />
                    QR Code
                  </h3>
                </div>
                <div className="card-body text-center">
                  <img src={bill.qrCode.qrImageUrl} alt="QR Code" className="mx-auto w-48 h-48" />
                  <p className="mt-2 text-xs text-slate-500">Scan to pay</p>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-medium">Actions</h3>
              </div>
              <div className="card-body space-y-3">
                {bill.status === 'draft' && (
                  <button onClick={() => issueBill.mutate()} className="btn-primary w-full" disabled={issueBill.isLoading}>
                    <CheckCircleIcon className="h-4 w-4 mr-2" />
                    Issue Bill
                  </button>
                )}

                {bill.status !== 'paid' && bill.status !== 'cancelled' && bill.status !== 'expired' && (
                  <>
                    <button
                      onClick={() => toggleBlock.mutate(bill.status === 'blocked' ? 'unblock' : 'block')}
                      className={`w-full ${bill.status === 'blocked' ? 'btn-secondary' : 'btn-danger'}`}
                      disabled={toggleBlock.isLoading}
                    >
                      {bill.status === 'blocked' ? (
                        <><LockOpenIcon className="h-4 w-4 mr-2" />Unblock Bill</>
                      ) : (
                        <><LockClosedIcon className="h-4 w-4 mr-2" />Block Bill</>
                      )}
                    </button>

                    <button onClick={() => cancelBill.mutate()} className="btn-danger w-full" disabled={cancelBill.isLoading}>
                      <XCircleIcon className="h-4 w-4 mr-2" />
                      Cancel Bill
                    </button>
                  </>
                )}

                <button onClick={() => generateSecureLink.mutate()} className="btn-secondary w-full" disabled={generateSecureLink.isLoading}>
                  <ShareIcon className="h-4 w-4 mr-2" />
                  Generate Secure Link
                </button>

                <button onClick={() => window.print()} className="btn-secondary w-full">
                  <PrinterIcon className="h-4 w-4 mr-2" />
                  Print Bill
                </button>
              </div>
            </div>

            {/* Bill History (Status Audit Trail) */}
            <div className="card">
              <div className="card-header border-b border-slate-100 dark:border-slate-800 pb-3">
                <h3 className="text-lg font-medium text-slate-800 dark:text-slate-100">Bill History</h3>
              </div>
              <div className="card-body">
                <div className="space-y-4 max-h-[300px] overflow-y-auto pr-2">
                  {!bill.statusHistory || bill.statusHistory.length === 0 ? (
                    <p className="text-sm text-slate-500">No history available.</p>
                  ) : (
                    bill.statusHistory.map((history, index) => (
                      <div key={index} className="flex items-start gap-4 flex-col sm:flex-row sm:items-center sm:justify-between border-b border-slate-100 dark:border-slate-800 pb-3 last:border-0 last:pb-0">
                        <div className="flex items-start gap-3">
                          <div className="mt-1">
                            {history.newStatus === 'paid' ? (
                              <CheckCircleIcon className="h-5 w-5 text-emerald-500" />
                            ) : history.newStatus === 'blocked' ? (
                              <LockClosedIcon className="h-5 w-5 text-rose-500" />
                            ) : (
                              <div className="h-5 w-5 rounded-full bg-slate-200 dark:bg-slate-700" />
                            )}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                              {history.previousStatus ? `${history.previousStatus} → ${history.newStatus}` : history.newStatus}
                            </p>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{history.reason || 'Status updated'}</p>
                            <p className="text-[11px] text-slate-400 dark:text-slate-500 font-mono mt-1">
                              {new Date(history.createdAt).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Payment History */}
            <div className="card">
              <div className="card-header border-b border-slate-100 dark:border-slate-800 pb-3">
                <h3 className="text-lg font-medium text-slate-800 dark:text-slate-100">Payment History</h3>
              </div>
              <div className="card-body">
                <div className="space-y-4 max-h-[300px] overflow-y-auto pr-2">
                  {!bill.transactions || bill.transactions.length === 0 ? (
                    <p className="text-sm text-slate-500">No payments found.</p>
                  ) : (
                    bill.transactions.map((txn, index) => (
                      <div key={index} className="flex items-start gap-4 flex-col sm:flex-row sm:items-center sm:justify-between border-b border-slate-100 dark:border-slate-800 pb-3 last:border-0 last:pb-0">
                        <div>
                          <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                            {txn.bankChannel || 'Bank Transfer'} <span className="text-slate-400 dark:text-slate-500 font-normal">| Ref:</span> <span className="font-mono text-xs text-slate-600 dark:text-slate-400">{txn.transactionNumber}</span>
                          </p>
                          <p className={`text-xs mt-0.5 font-bold ${['00', '0000', 'success', 'paid', 'approved'].includes(txn.statusCode?.toLowerCase()) ? 'text-emerald-500' : 'text-amber-500'}`}>
                            {['00', '0000', 'success', 'paid', 'approved'].includes(txn.statusCode?.toLowerCase()) ? 'Successful' : txn.statusCode} {txn.statusMessage && `- ${txn.statusMessage}`}
                          </p>
                          <p className="text-[11px] text-slate-400 dark:text-slate-500 font-mono mt-1">
                            {new Date(txn.postedAt || txn.paymentDate).toLocaleString()}
                          </p>
                        </div>
                        <div className="text-right sm:text-right mt-2 sm:mt-0">
                          <span className="text-sm font-bold text-slate-800 dark:text-slate-200">
                            Rs {parseFloat(txn.paymentAmount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                          {txn.isDuplicate && (
                            <span className="block text-[10px] font-semibold text-rose-500 uppercase tracking-wider mt-1">Duplicate</span>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Print Only View */}
      <div className="hidden print:block bg-white text-black p-8 font-sans max-w-4xl mx-auto border border-slate-300 rounded-lg relative">
        {/* Header */}
        <div className="flex justify-between items-center border-b-2 border-slate-300 pb-4 mb-6">
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">XPERT PAYMENTS</h1>
            <p className="text-xs text-slate-500 font-medium">Digital Billing & QR Payment System</p>
          </div>
          <div className="text-right">
            <h2 className="text-xl font-bold text-slate-800">PAYMENT CHALLAN</h2>
            <p className="text-sm font-mono text-slate-500">{bill.billId}</p>
          </div>
        </div>

        {/* Status Stamp if Paid */}
        {bill.status === 'paid' && (
          <div className="absolute top-12 left-1/2 -translate-x-1/2 border-4 border-green-600 text-green-600 font-extrabold text-3xl uppercase tracking-widest px-6 py-2 rounded-lg rotate-12 opacity-80">
            PAID
          </div>
        )}

        {bill.status === 'cancelled' && (
          <div className="absolute top-12 left-1/2 -translate-x-1/2 border-4 border-red-600 text-red-600 font-extrabold text-3xl uppercase tracking-widest px-6 py-2 rounded-lg rotate-12 opacity-80">
            CANCELLED
          </div>
        )}

        {/* Customer & Challan Grid */}
        <div className="grid grid-cols-2 gap-8 mb-8">
          <div className="border border-slate-200 rounded-md p-4 bg-slate-50">
            <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2 border-b pb-1">Customer Information</h3>
            <table className="w-full text-sm">
              <tbody>
                <tr className="border-b border-slate-100">
                  <td className="py-1.5 font-medium text-slate-500">Consumer No:</td>
                  <td className="py-1.5 font-semibold font-mono">{bill.customer?.consumerNumber || 'N/A'}</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-1.5 font-medium text-slate-500">Name:</td>
                  <td className="py-1.5 font-semibold">{bill.consumerName}</td>
                </tr>
                {bill.customer?.mobile && (
                  <tr className="border-b border-slate-100">
                    <td className="py-1.5 font-medium text-slate-500">Mobile:</td>
                    <td className="py-1.5 font-mono">{bill.customer?.mobile}</td>
                  </tr>
                )}
                {bill.customer?.email && (
                  <tr className="border-b border-slate-100">
                    <td className="py-1.5 font-medium text-slate-500">Email:</td>
                    <td className="py-1.5">{bill.customer?.email}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="border border-slate-200 rounded-md p-4 bg-slate-50">
            <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2 border-b pb-1">Challan Details</h3>
            <table className="w-full text-sm">
              <tbody>
                <tr className="border-b border-slate-100">
                  <td className="py-1.5 font-medium text-slate-500">Challan No:</td>
                  <td className="py-1.5 font-bold font-mono text-base">{bill.challanNo}</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-1.5 font-medium text-slate-500">Billing Month:</td>
                  <td className="py-1.5 font-semibold">{bill.billingMonth}</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-1.5 font-medium text-slate-500">Due Date:</td>
                  <td className="py-1.5 font-bold text-red-650">{new Date(bill.dueDate).toLocaleDateString()}</td>
                </tr>
                <tr>
                  <td className="py-1.5 font-medium text-slate-500">Issue Date:</td>
                  <td className="py-1.5 font-semibold">{new Date(bill.createdAt).toLocaleDateString()}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Pricing Summary */}
        <div className="border border-slate-200 rounded-md overflow-hidden mb-8">
          <table className="w-full text-left text-sm border-collapse">
            <thead>
              <tr className="bg-slate-100 border-b border-slate-200">
                <th className="py-2.5 px-4 font-bold text-slate-700">Description</th>
                <th className="py-2.5 px-4 font-bold text-slate-700 text-right">Amount (PKR)</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-slate-150">
                <td className="py-3 px-4">
                  <p className="font-semibold text-slate-800">Utility Billing Charge</p>
                  <p className="text-xs text-slate-505">Standard processor charges apply if not paid within due date</p>
                </td>
                <td className="py-3 px-4 text-right font-semibold text-slate-800">
                  PKR {parseFloat(bill.feeBeforeDueDate).toLocaleString()}
                </td>
              </tr>
              <tr className="bg-slate-50 font-bold border-t-2 border-slate-200">
                <td className="py-3 px-4 text-slate-700">PAYABLE BEFORE DUE DATE:</td>
                <td className="py-3 px-4 text-right text-green-600 text-lg">
                  PKR {parseFloat(bill.feeBeforeDueDate).toLocaleString()}
                </td>
              </tr>
              <tr className="bg-red-50/50 font-bold border-t border-slate-200">
                <td className="py-3 px-4 text-slate-700">PAYABLE AFTER DUE DATE:</td>
                <td className="py-3 px-4 text-right text-red-600 text-lg">
                  PKR {parseFloat(bill.feeAfterDueDate).toLocaleString()}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Payment QR Section */}
        <div className="grid grid-cols-3 gap-6 items-center border-t border-b border-slate-200 py-6 mb-8 bg-slate-50 p-4 rounded-md">
          <div className="col-span-2 space-y-2">
            <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider">How to Pay</h3>
            <ul className="list-disc pl-4 text-xs text-slate-600 space-y-1.5">
              <li>Open your mobile banking or wallet application (e.g. EasyPaisa, JazzCash, HBL, ABL).</li>
              <li>Navigate to the <strong>Scan QR</strong> feature in the application.</li>
              <li>Scan the QR code displayed on the right of this challan.</li>
              <li>Verify the payment details (Challan Number & Amount) and click Pay.</li>
              <li>Keep the transaction receipt of your payment app for verification.</li>
            </ul>
          </div>
          <div className="text-center font-sans border-l border-slate-200 pl-6">
            {bill.qrCode ? (
              <>
                <img src={bill.qrCode.qrImageUrl} alt="Payment QR" className="mx-auto w-32 h-32 mb-1 border p-1 bg-white rounded-md shadow-sm" />
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-tight">Scan with any Bank App</p>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center h-32 border-2 border-dashed border-slate-300 rounded-md">
                <p className="text-xs text-slate-400 font-medium">QR Loading...</p>
              </div>
            )}
          </div>
        </div>

        {/* Signatures & Footer */}
        <div className="grid grid-cols-2 gap-8 text-center pt-8">
          <div>
            <div className="border-t border-slate-350 w-48 mx-auto mt-8 font-semibold"></div>
            <p className="text-xs text-slate-500 mt-2 font-medium">Prepared By</p>
          </div>
          <div>
            <div className="border-t border-slate-350 w-48 mx-auto mt-8 font-semibold"></div>
            <p className="text-xs text-slate-500 mt-2 font-medium">Authorized Signature</p>
          </div>
        </div>

        <div className="text-[10px] text-center text-slate-400 mt-12 border-t pt-4">
          This is a system generated document. No physical stamp is required. For support, contact support@xpertpayments.com.
        </div>
      </div>
    </>
  );
}
